import { useTranslation } from 'react-i18next';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import RowActionsMenu from '../../Fundamentals/RowActionsMenu/RowActionsMenu';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import Spinner from '../../Fundamentals/Spinner/Spinner';
import PackageStatusBadge from './PackageStatusBadge';
import ScheduledChangeStatusBadge from './ScheduledChangeStatusBadge';
import dateUtils from '../../../Utils/dateUtils';
import { versionStatusFromName } from '../../../Constants/VOSS/vossVersionStatus';
import { isPendingScheduledChange, sortScheduledChanges } from './scheduledChangeUtils';
import type { PackageScheduledChange } from './Packages.types';

interface PackageScheduledChangesTabProps {
    changes: PackageScheduledChange[] | undefined;
    isLoading: boolean;
    // a version with no forward transition cannot get new changes, but its pending ones can still be edited or cancelled
    canSchedule: boolean;
    onAdd: () => void;
    onEdit: (change: PackageScheduledChange) => void;
    onCancel: (change: PackageScheduledChange) => void;
}

const PackageScheduledChangesTab = ({ changes, isLoading, canSchedule, onAdd, onEdit, onCancel }: PackageScheduledChangesTabProps) => {
    const { t } = useTranslation();

    const list = () => {
        if (changes === undefined) return isLoading ? <Spinner /> : null;

        if (changes.length === 0)
            return (
                <EmptyState
                    icon="calendar"
                    title={t('packages.noScheduledChanges') ?? ''}
                    description={(canSchedule ? t('packages.noScheduledChangesDescription') : t('packages.noScheduledChangesReadOnly')) ?? ''}
                />
            );

        return (
            <table className="table table-stripe skynet-table-no-divider">
                <thead>
                    <tr>
                        <th className="col-2">{t('packages.scheduledDate')}</th>
                        <th className="col-3">{t('packages.change')}</th>
                        <th className="col-3">{t('packages.note')}</th>
                        <th className="col-2">{t('packages.createdBy')}</th>
                        <th className="col-1">{t('common.status')}</th>
                        <th className="col-1">
                            <span className="sr-only">{t('packages.scheduledChangeActions')}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortScheduledChanges(changes).map((change) => {
                        const targetStatus = versionStatusFromName(change.package?.status);
                        const newName = change.package?.name ?? '';
                        const note = change.note ?? '';
                        const reference = change.reference ?? '';

                        return (
                            <tr key={change.id}>
                                <td className="py-12 font-weight-600 text-nowrap">{dateUtils.formatDateOrPlaceholder(change.scheduledDate, '—')}</td>
                                <td className="py-12">
                                    <div className="d-flex align-items-center gap-8 flex-wrap">
                                        {targetStatus !== undefined && <PackageStatusBadge status={targetStatus} />}
                                        {newName.length > 0 && <span className="font-13">{t('packages.renameTo', { name: newName })}</span>}
                                        {targetStatus === undefined && newName.length === 0 && '—'}
                                    </div>
                                </td>
                                <td className="py-12">
                                    <span className={note.length > 0 ? 'font-13' : 'text-secondary'}>{note.length > 0 ? note : '—'}</span>
                                </td>
                                <td className="py-12">
                                    <span className={reference.length > 0 ? 'font-13' : 'text-secondary'}>{reference.length > 0 ? reference : '—'}</span>
                                </td>
                                <td className="py-12">
                                    <ScheduledChangeStatusBadge status={change.status} />
                                </td>
                                <td className="py-12 text-right">
                                    {isPendingScheduledChange(change) && (
                                        <RowActionsMenu
                                            label={t('packages.scheduledChangeActions') ?? ''}
                                            actions={[
                                                { id: 'edit', label: t('packages.editScheduledChange') ?? '', icon: 'edit', onClick: () => onEdit(change) },
                                                {
                                                    id: 'cancel',
                                                    label: t('packages.cancelScheduledChange') ?? '',
                                                    icon: 'close',
                                                    destructive: true,
                                                    onClick: () => onCancel(change),
                                                },
                                            ]}
                                        />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <>
            <div className="d-flex align-items-start justify-content-between gap-16 flex-wrap mb-16">
                <div>
                    <h3 className="heading-lg mb-4">{t('packages.scheduledChangesTitle')}</h3>
                    <p className="text-secondary font-13 mb-0">{t('packages.scheduledChangesDescription')}</p>
                </div>
                {canSchedule && (
                    <button type="button" className="btn btn-secondary" onClick={onAdd}>
                        <SpirisIcon name="plus" size="sm" className="mr-8" />
                        {t('packages.scheduleChange')}
                    </button>
                )}
            </div>
            {list()}
        </>
    );
};

export default PackageScheduledChangesTab;
